
import React from 'react';
import { useLanguage } from "@/hooks/useLanguage";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Package } from "lucide-react";
import { ReleaseItem } from '@/types/dashboard';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { pl, enUS } from 'date-fns/locale';
import { Badge } from '@/components/ui/badge';

interface ReleasesCardProps {
  releases: ReleaseItem[];
  isLoading: boolean;
}

export function ReleasesCard({ releases, isLoading }: ReleasesCardProps) {
  const { t, language } = useLanguage();
  const locale = language === "pl" ? pl : enUS;

  return (
    <Card className="border-border/50 h-full bg-card/50 backdrop-blur-sm hover:shadow-lg transition-all duration-300 hover:border-primary/50 group flex flex-col">
      <CardHeader className="pb-2">
        <CardTitle className="text-xl flex items-center">
          <div className="p-2 rounded-lg bg-primary/10 mr-3 group-hover:bg-primary/20 transition-colors">
            <Package className="h-5 w-5 text-primary" />
          </div>
          <Link to="/releases" className="hover:text-primary transition-colors flex-1">
            {t('dashboard.latest_releases')}
          </Link>
        </CardTitle>
      </CardHeader>
      <CardContent className="flex-1 min-h-0 p-2 overflow-hidden">
        {isLoading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex flex-col gap-2">
                <div className="h-5 w-3/4 bg-secondary/50 animate-pulse rounded-md" />
                <div className="h-4 w-1/4 bg-secondary/30 animate-pulse rounded-md" />
              </div>
            ))}
          </div>
        ) : releases && releases.length > 0 ? (
          <div className="flex flex-col h-full gap-2">
            {releases.map((release, index) => (
              <a
                key={index}
                href={release.url_projektu || `https://github.com/search?q=${release.nazwa_projektu}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col p-2 rounded-lg hover:bg-secondary/50 transition-colors group/item flex-1 min-h-0 justify-center"
              >
                <div className="flex justify-between items-start mb-1 shrink-0">
                  <span className="font-medium group-hover/item:text-primary transition-colors line-clamp-1">{release.nazwa_projektu}</span>
                  <Badge variant="outline" className="ml-2 font-mono text-xs bg-background/50 flex-shrink-0 h-5">
                    {release.wersja}
                  </Badge>
                </div>
                <div className="text-sm text-muted-foreground mt-auto pt-1 shrink-0 flex justify-end">
                  {t('releases.release_date')}: {format(new Date(release.data_wydania), 'PP', { locale })}
                </div>
              </a>
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground text-sm">{t('common.no_results')}</p>
        )}
      </CardContent>
    </Card>
  );
}
